import { Component, ChangeDetectionStrategy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { AdminService } from '@generated/services/admin.service';
import { SettingDto } from '@generated/models/setting-dto';
import { SettingKey } from '@generated/models/setting-key';
import { NotificationService } from '@core/services/notification.service';
import { DatePipe } from '@angular/common';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';

@Component({
  selector: 'app-admin-settings',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    CommonModule,
    FormsModule,
    ButtonModule,
    TableModule,
    DrawerModule,
    InputTextModule,
    ProgressSpinnerModule,
    TooltipModule,
    TagModule,
    DatePipe
  ],
  templateUrl: './admin-settings.html',
  styleUrl: './admin-settings.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminSettingsComponent implements OnInit {
  private adminService = inject(AdminService);
  private notificationService = inject(NotificationService);

  readonly settings = signal<SettingDto[]>([]);
  readonly isLoading = signal<boolean>(false);

  readonly drawerVisible = signal<boolean>(false);
  readonly editingKey = signal<SettingKey | null>(null);
  readonly editingValue = signal<string>('');
  readonly isSaving = signal<boolean>(false);

  ngOnInit(): void {
    this.loadSettings();
  }

  private loadSettings(): void {
    this.isLoading.set(true);
    this.adminService.apiAdminSettingsGet().subscribe({
      next: (settings) => {
        this.settings.set(settings || []);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Failed to load settings:', error);
        this.notificationService.showError(
          $localize`:@@adminSettings.notify.loadFail:Failed to load system settings`
        );
        this.isLoading.set(false);
      }
    });
  }

  edit(setting: SettingDto): void {
    if (!setting.key) return;
    this.editingKey.set(setting.key);
    this.editingValue.set(setting.value ?? '');
    this.drawerVisible.set(true);
  }

  closeDrawer(): void {
    if (this.isSaving()) return;
    this.drawerVisible.set(false);
    this.editingKey.set(null);
    this.editingValue.set('');
  }

  save(): void {
    const key = this.editingKey();
    if (!key || this.isSaving()) return;

    this.isSaving.set(true);
    this.adminService.apiAdminSettingsKeyPut({
      key,
      body: { value: this.editingValue() || null }
    }).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.drawerVisible.set(false);
        this.editingKey.set(null);
        this.editingValue.set('');
        this.notificationService.showSuccess(
          $localize`:@@adminSettings.notify.saved:Setting saved`
        );
        this.loadSettings();
      },
      error: (error) => {
        this.isSaving.set(false);
        console.error('Failed to save setting:', error);
        const msg = this.extractProblemDetailsMessage(error)
          ?? $localize`:@@adminSettings.notify.saveFail:Failed to save setting`;
        this.notificationService.showError(msg);
      }
    });
  }

  onValueInput(value: string): void {
    this.editingValue.set(value);
  }

  private extractProblemDetailsMessage(error: unknown): string | null {
    const err = error as { error?: { detail?: string; title?: string; errors?: Record<string, string[]> } } | null;
    if (!err?.error) return null;
    const errors = err.error.errors;
    if (errors) {
      const all = Object.values(errors).flat();
      if (all.length > 0) return all.join('; ');
    }
    return err.error.detail || err.error.title || null;
  }
}
